import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

function isVideo(url) {
    if (! url) return false;
    return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);
}

function FakeChrome({ url, children }) {
    return (
        <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-foreground/5">
            <div className="flex items-center gap-1.5 border-b border-border/80 px-4 py-2.5">
                <span className="size-2.5 rounded-full bg-rose-400/80" />
                <span className="size-2.5 rounded-full bg-amber-400/80" />
                <span className="size-2.5 rounded-full bg-emerald-400/80" />
                <span className="ml-3 truncate font-mono text-[11px] text-muted-foreground">{url || 'mailer.sharkmonk.com'}</span>
            </div>
            <div className="relative">{children}</div>
        </div>
    );
}

// Hero media rotator with crossfade.
export function HeroShowcase({ landing = {} }) {
    const { t } = useT();
    const enabled = landing.hero_showcase_enabled !== false;
    const interval = Math.max(2, Number(landing.hero_showcase_interval) || 4.5);
    const slides = useMemo(() => {
        const raw = Array.isArray(landing.hero_showcase_slides) ? landing.hero_showcase_slides : [];
        return raw.filter((s) => s && !! s.src);
    }, [landing.hero_showcase_slides]);

    const [idx, setIdx] = useState(0);
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const prefersReducedMotion = useMemo(() => {
        if (typeof window === 'undefined' || ! window.matchMedia) return false;
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (! el || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }
        const obs = new IntersectionObserver(
            (entries) => setInView(entries.some((e) => e.isIntersecting)),
            { threshold: 0.2 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (! enabled || prefersReducedMotion || ! inView || slides.length <= 1) return undefined;
        const id = window.setInterval(() => setIdx((i) => (i + 1) % slides.length), interval * 1000);
        return () => window.clearInterval(id);
    }, [enabled, prefersReducedMotion, inView, slides.length, interval]);

    if (! enabled || ! slides.length) return null;

    const active = slides[idx % slides.length];

    return (
        <div ref={ref} className="relative">
            <FakeChrome url={landing.hero_showcase_chrome_url}>
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-card">
                    {slides.map((slide, i) => {
                        const isActive = i === idx % slides.length;
                        return (
                            <div
                                key={i}
                                aria-hidden={! isActive}
                                className={cn(
                                    'absolute inset-0 transition-opacity duration-700 ease-out',
                                    isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none',
                                )}
                            >
                                {isVideo(slide.src) ? (
                                    <video
                                        src={slide.src}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        className="size-full object-cover"
                                    />
                                ) : (
                                    <img
                                        src={slide.src}
                                        alt={slide.label || ''}
                                        className="size-full object-cover"
                                        loading={i === 0 ? 'eager' : 'lazy'}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </FakeChrome>

            {slides.length > 1 && (
                <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold tracking-tight text-foreground">{active.label}</p>
                        {active.caption && (
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">{active.caption}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5">
                        {slides.map((s, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setIdx(i)}
                                aria-label={s.label || t('landing.hero_showcase.slide_n', null, { n: i + 1 })}
                                aria-current={i === idx % slides.length}
                                className={cn(
                                    'h-1.5 rounded-full transition-all duration-300',
                                    i === idx % slides.length ? 'w-6 bg-accent' : 'w-1.5 bg-border hover:bg-muted-foreground/50',
                                )}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
